import React, { useState, useEffect, useRef } from "react";
import { ReligionTab } from "../types";
import { createAudioContext } from "../lib/audio-utils";
import { Volume2, VolumeX, Play, Pause, Music, Sliders, Sparkles } from "lucide-react";

interface RitualAmbientSoundProps {
  activeReligion: ReligionTab;
}

interface SoundscapeConfig {
  title: string;
  description: string;
  baseFreq: number;
  harmonics: number[];
  type: OscillatorType;
  filterFreq: number;
}

const SOUNDSCAPES: Record<ReligionTab, SoundscapeConfig> = {
  hinduism: {
    title: "Vedic Tanpura & Temple Drone",
    description: "Resonant C# Tanpura harmonic drone & temple bell resonance.",
    baseFreq: 138.59, // C#3
    harmonics: [1, 1.5, 2, 3],
    type: "sine",
    filterFreq: 800
  },
  islam: {
    title: "Meditative Ney & Tranquil Pad",
    description: "Gentle oriental Ney flute warmth & peaceful ambient airflow.",
    baseFreq: 220, // A3
    harmonics: [1, 1.25, 1.5, 2],
    type: "triangle",
    filterFreq: 600
  },
  buddhism: {
    title: "Tibetan Singing Bowl Atmosphere",
    description: "432Hz deep bronze singing bowl resonance with rich overtones.",
    baseFreq: 108, // A2 (432Hz harmonic)
    harmonics: [1, 2, 3, 4],
    type: "sine",
    filterFreq: 1200
  },
  christianity: {
    title: "Cathedral Pipe Organ & Gregorian Pad",
    description: "Warm, rich sacred organ drone simulating sacred acoustic spaces.",
    baseFreq: 130.81, // C3
    harmonics: [1, 1.333, 1.5, 2],
    type: "sawtooth",
    filterFreq: 450
  },
  judaism: {
    title: "Sacred Strings & Shofar Resonance",
    description: "Deep ancient resonant horn & string instrument harmonics.",
    baseFreq: 146.83, // D3
    harmonics: [1, 1.5, 2.25, 3],
    type: "triangle",
    filterFreq: 700
  },
  sikhism: {
    title: "Gurbani Saaj & Harmonium Tanpura Drone",
    description: "Deep devotional Rabab & Harmonium acoustic resonance.",
    baseFreq: 164.81, // E3
    harmonics: [1, 1.5, 2, 3],
    type: "sine",
    filterFreq: 850
  },
  taoism: {
    title: "Taoist Bamboo Flute & Water Flow",
    description: "Effortless Wu Wei bamboo flute tones & mountain stream stream harmonics.",
    baseFreq: 196.00, // G3
    harmonics: [1, 1.25, 1.5, 2],
    type: "sine",
    filterFreq: 900
  },
  jainism: {
    title: "Navkar Peace Bell & Meditative Pure Tone",
    description: "Ultra-pure 432Hz sine chime for Ahimsa meditation.",
    baseFreq: 108,
    harmonics: [1, 2, 3, 4],
    type: "sine",
    filterFreq: 1000
  },
  bahai: {
    title: "Bahá'í Universal Sacred Chime",
    description: "Ethereal 587Hz D5 chime harmonic for world oneness.",
    baseFreq: 293.66,
    harmonics: [1, 2, 3],
    type: "sine",
    filterFreq: 1100
  },
  zoroastrianism: {
    title: "Sacred Fire (Atash) Resonance Drone",
    description: "Warm acoustic fire drone & 432Hz Avestan harmonic resonance.",
    baseFreq: 108,
    harmonics: [1, 1.5, 2, 3],
    type: "triangle",
    filterFreq: 650
  },
  shinto: {
    title: "Shinto Mountain Spring & Gagaku Chime",
    description: "Clear mountain stream water flow & sacred shrine bell chime.",
    baseFreq: 220,
    harmonics: [1, 2, 3, 4],
    type: "sine",
    filterFreq: 1200
  },
  indigenous_americas: {
    title: "Sacred Cedar Flute & Four Directions Wind",
    description: "Heartland cedar wood flute drone & gentle mountain breeze.",
    baseFreq: 174.61, // F3
    harmonics: [1, 1.5, 2],
    type: "triangle",
    filterFreq: 750
  },
  shamanism: {
    title: "432Hz Earth Drum & Pachamama Heartbeat",
    description: "108Hz/432Hz deep resonant drum heartbeat pulse.",
    baseFreq: 108,
    harmonics: [1, 2, 3],
    type: "sine",
    filterFreq: 500
  },
  universal: {
    title: "528Hz Solfeggio Harmony & Gentle Rain",
    description: "Universal miracle healing frequency with synthesized white-noise rain.",
    baseFreq: 264, // 528Hz octave
    harmonics: [1, 2, 2.5, 3],
    type: "sine",
    filterFreq: 1000
  }
};

export type ReverbPreset = "cave" | "temple" | "cathedral" | "zendo" | "dry";

export default function RitualAmbientSound({ activeReligion }: RitualAmbientSoundProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.4);
  const [reverbPreset, setReverbPreset] = useState<ReverbPreset>("cave");
  const [schumannEnabled, setSchumannEnabled] = useState<boolean>(true);
  const [thetaEnabled, setThetaEnabled] = useState<boolean>(false);

  const soundscape = SOUNDSCAPES[activeReligion] || SOUNDSCAPES.universal;

  const audioCtxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const filterRef = useRef<BiquadFilterNode | null>(null);

  // Auto-suspend AudioContext when tab becomes hidden for mobile battery efficiency
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (audioCtxRef.current && audioCtxRef.current.state === 'running') {
          audioCtxRef.current.suspend();
        }
      } else {
        if (isPlaying && audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
          audioCtxRef.current.resume();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying]);

  // Generate synthetic impulse response buffer for ConvolverNode Reverb
  const createImpulseResponse = (ctx: AudioContext, durationSec: number, decay: number) => {
    const rate = ctx.sampleRate;
    const length = rate * durationSec;
    const impulse = ctx.createBuffer(2, length, rate);
    const left = impulse.getChannelData(0);
    const right = impulse.getChannelData(1);

    for (let i = 0; i < length; i++) {
      const n = i;
      const dec = Math.pow(1 - n / length, decay);
      left[i] = (Math.random() * 2 - 1) * dec;
      right[i] = (Math.random() * 2 - 1) * dec;
    }
    return impulse;
  };

  // Stop current ambient nodes
  const stopAmbientSound = () => {
    oscillatorsRef.current.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {}
    });
    oscillatorsRef.current = [];

    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      try {
        audioCtxRef.current.suspend();
      } catch (e) {}
    }
    setIsPlaying(false);
  };

  // Start ambient soundscape Web Audio synthesis
  const startAmbientSound = () => {
    try {
      stopAmbientSound();

      const ctx = createAudioContext();
      if (!ctx) return;
      audioCtxRef.current = ctx;

      if (ctx.state === "suspended") {
        ctx.resume();
      }

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(volume, ctx.currentTime);
      masterGain.connect(ctx.destination);
      masterGainRef.current = masterGain;

      // 3D Spatial Audio Convolver Node (Impulse Response)
      let finalOutputNode: AudioNode = masterGain;

      if (reverbPreset !== "dry") {
        const convolver = ctx.createConvolver();
        const duration = reverbPreset === "cathedral" ? 4.5 : reverbPreset === "cave" ? 3.0 : reverbPreset === "temple" ? 2.0 : 1.2;
        const decay = reverbPreset === "cathedral" ? 2.5 : reverbPreset === "cave" ? 3.5 : 2.0;
        convolver.buffer = createImpulseResponse(ctx, duration, decay);
        convolver.connect(masterGain);

        const wetGain = ctx.createGain();
        wetGain.gain.setValueAtTime(0.35, ctx.currentTime);
        wetGain.connect(convolver);
        finalOutputNode = wetGain;
      }

      // Lowpass filter for smooth warmth
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(soundscape.filterFreq, ctx.currentTime);
      filter.connect(finalOutputNode);
      if (reverbPreset !== "dry") {
        filter.connect(masterGain); // dry mix
      }
      filterRef.current = filter;

      const newOscs: OscillatorNode[] = [];

      // Create multi-harmonic drone layer
      soundscape.harmonics.forEach((multiplier, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = soundscape.type;
        osc.frequency.setValueAtTime(soundscape.baseFreq * multiplier, ctx.currentTime);

        const detuneAmount = (i % 2 === 0 ? 1 : -1) * (i * 3 + 2);
        osc.detune.setValueAtTime(detuneAmount, ctx.currentTime);

        const harmonicGain = 0.25 / (i + 1);
        gain.gain.setValueAtTime(harmonicGain, ctx.currentTime);

        osc.connect(gain);
        gain.connect(filter);

        osc.start();
        newOscs.push(osc);
      });

      // Schumann Earth Resonance 7.83Hz Sub-harmonic overlay
      if (schumannEnabled) {
        const schumannOsc = ctx.createOscillator();
        const schumannGain = ctx.createGain();
        schumannOsc.type = "sine";
        schumannOsc.frequency.setValueAtTime(7.83, ctx.currentTime);
        schumannGain.gain.setValueAtTime(0.08, ctx.currentTime);
        schumannOsc.connect(schumannGain);
        schumannGain.connect(masterGain);
        schumannOsc.start();
        newOscs.push(schumannOsc);
      }

      // Theta Binaural 3.5Hz pulse overlay
      if (thetaEnabled) {
        const thetaL = ctx.createOscillator();
        const thetaR = ctx.createOscillator();
        const thetaGain = ctx.createGain();

        thetaL.type = "sine";
        thetaR.type = "sine";
        thetaL.frequency.setValueAtTime(108, ctx.currentTime);
        thetaR.frequency.setValueAtTime(111.5, ctx.currentTime); // 3.5Hz differential
        thetaGain.gain.setValueAtTime(0.06, ctx.currentTime);

        thetaL.connect(thetaGain);
        thetaR.connect(thetaGain);
        thetaGain.connect(masterGain);

        thetaL.start();
        thetaR.start();
        newOscs.push(thetaL, thetaR);
      }

      oscillatorsRef.current = newOscs;
      setIsPlaying(true);
    } catch (e) {
      console.warn("Ambient audio error:", e);
    }
  };

  // Toggle Play / Pause
  const togglePlay = () => {
    if (isPlaying) {
      stopAmbientSound();
    } else {
      startAmbientSound();
    }
  };

  // Handle Volume Change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVol = parseFloat(e.target.value);
    setVolume(newVol);
    if (masterGainRef.current && audioCtxRef.current) {
      masterGainRef.current.gain.setValueAtTime(newVol, audioCtxRef.current.currentTime);
    }
  };

  // Restart soundscape when activeReligion changes if currently playing
  useEffect(() => {
    if (isPlaying) {
      startAmbientSound();
    }
  }, [activeReligion]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopAmbientSound();
    };
  }, []);

  return (
    <div className="flex flex-wrap items-center gap-3 bg-stone-900/90 border border-amber-500/30 rounded-2xl p-2.5 px-4 shadow-lg backdrop-blur-md">
      <div className="flex items-center gap-2">
        <button
          onClick={togglePlay}
          className={`p-2.5 rounded-xl border transition-all flex items-center justify-center ${
            isPlaying
              ? "bg-amber-500/20 border-amber-400 text-amber-300 shadow-md shadow-amber-500/20 scale-105"
              : "bg-stone-800 border-stone-700 text-stone-300 hover:text-stone-100 hover:bg-stone-700"
          }`}
          title={isPlaying ? "Pause Ambient Soundscape" : "Play Ambient Soundscape"}
        >
          {isPlaying ? <Pause className="w-4 h-4 fill-amber-300" /> : <Play className="w-4 h-4 fill-stone-300 ml-0.5" />}
        </button>

        <div className="hidden sm:block space-y-0.5 max-w-[180px]">
          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-200 truncate">
            <Music className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="truncate">{soundscape.title}</span>
          </div>
          <p className="text-[10px] text-stone-400 truncate">{soundscape.description}</p>
        </div>
      </div>

      {/* 3D Reverb Space Selection */}
      <div className="flex items-center gap-1.5 border-l border-stone-800 pl-3">
        <span className="text-[10px] font-mono text-stone-400 hidden md:inline">Reverb:</span>
        <select
          value={reverbPreset}
          onChange={(e) => {
            setReverbPreset(e.target.value as ReverbPreset);
            if (isPlaying) startAmbientSound();
          }}
          className="bg-stone-950 border border-stone-800 text-[10px] font-mono text-amber-300 rounded-lg p-1 px-1.5 outline-none cursor-pointer"
        >
          <option value="cave">🏔️ Himalayan Cave</option>
          <option value="temple">🛕 Temple Courtyard</option>
          <option value="cathedral">⛪ Gothic Cathedral</option>
          <option value="zendo">🧘 Zen Zendo</option>
          <option value="dry">Dry Acoustic</option>
        </select>
      </div>

      {/* Schumann 7.83Hz & Theta Overlays */}
      <div className="flex items-center gap-1.5 border-l border-stone-800 pl-3">
        <button
          onClick={() => {
            setSchumannEnabled(!schumannEnabled);
            if (isPlaying) startAmbientSound();
          }}
          className={`px-2 py-1 rounded text-[9px] font-mono font-bold transition border ${
            schumannEnabled
              ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
              : "bg-stone-950 text-stone-500 border-stone-800"
          }`}
          title="7.83Hz Earth Schumann Resonance"
        >
          7.83Hz Earth
        </button>

        <button
          onClick={() => {
            setThetaEnabled(!thetaEnabled);
            if (isPlaying) startAmbientSound();
          }}
          className={`px-2 py-1 rounded text-[9px] font-mono font-bold transition border ${
            thetaEnabled
              ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/40"
              : "bg-stone-950 text-stone-500 border-stone-800"
          }`}
          title="3.5Hz Binaural Theta Wave"
        >
          3.5Hz Theta
        </button>
      </div>

      {/* Volume Slider */}
      <div className="flex items-center gap-2 border-l border-stone-800 pl-3">
        <button
          onClick={() => {
            const nextVol = volume === 0 ? 0.4 : 0;
            setVolume(nextVol);
            if (masterGainRef.current && audioCtxRef.current) {
              masterGainRef.current.gain.setValueAtTime(nextVol, audioCtxRef.current.currentTime);
            }
          }}
          className="text-stone-400 hover:text-amber-300 transition-colors"
        >
          {volume === 0 ? <VolumeX className="w-4 h-4 text-stone-500" /> : <Volume2 className="w-4 h-4 text-amber-400" />}
        </button>

        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={volume}
          onChange={handleVolumeChange}
          className="w-14 sm:w-20 h-1.5 bg-stone-950 rounded-lg appearance-none cursor-pointer accent-amber-400 border border-stone-800"
        />
      </div>
    </div>
  );
}
